import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Threshold (px) beyond which a horizontal drag counts as a swipe.
const SWIPE_MIN = 40;
// Ratio guard so a diagonal drag that's mostly vertical (i.e. user scrolling
// the page) never triggers a swipe.
const SWIPE_H_OVER_V = 1.2;

export default function ProductCard({ product, index }) {
  const colours = product.colours || [];
  const [idx, setIdx] = useState(0);
  // Real-time drag offset (px) — drives the live drag preview so the swipe
  // has feedback, not a dead thumb until you release.
  const [dragX, setDragX] = useState(0);
  const touch = useRef({ startX: 0, startY: 0, active: false, locked: null });

  const current = colours[idx] || {};
  const activeImage = current.image || product.swatch;

  const goto = (n) => {
    const total = colours.length || 1;
    setIdx(((n % total) + total) % total);
  };

  const onTouchStart = (e) => {
    if (colours.length < 2 || e.touches.length !== 1) return;
    const t = e.touches[0];
    touch.current = { startX: t.clientX, startY: t.clientY, active: true, locked: null };
  };

  const onTouchMove = (e) => {
    if (!touch.current.active) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.startX;
    const dy = t.clientY - touch.current.startY;
    // First meaningful move decides the axis — locks in until touchend so
    // the user doesn't fight the browser between scroll and swipe.
    if (touch.current.locked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      touch.current.locked = Math.abs(dx) > Math.abs(dy) * SWIPE_H_OVER_V ? "h" : "v";
    }
    if (touch.current.locked === "h") {
      e.preventDefault();  // stop vertical scroll intercepting the swipe
      // Rubber-band the drag by 0.4× so it feels alive but restrained.
      setDragX(dx * 0.4);
    }
  };

  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    const finalDx = (e.changedTouches && e.changedTouches[0])
      ? e.changedTouches[0].clientX - touch.current.startX
      : 0;
    if (touch.current.locked === "h" && Math.abs(finalDx) > SWIPE_MIN) {
      goto(finalDx < 0 ? idx + 1 : idx - 1);
    }
    setDragX(0);
    touch.current = { startX: 0, startY: 0, active: false, locked: null };
  };

  return (
    <motion.article
      layout
      data-testid={`collection-card-${product.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="bg-[#1a1b1e]/60 border border-[#8C7355]/15 overflow-hidden group"
    >
      <div
        className="relative aspect-square overflow-hidden bg-[#0f1012] select-none"
        style={{ touchAction: colours.length > 1 ? "pan-y" : "auto" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onKeyDown={(e) => {
          if (colours.length < 2) return;
          if (e.key === "ArrowRight") { e.preventDefault(); goto(idx + 1); }
          if (e.key === "ArrowLeft")  { e.preventDefault(); goto(idx - 1); }
        }}
        tabIndex={colours.length > 1 ? 0 : -1}
        role={colours.length > 1 ? "region" : undefined}
        aria-label={colours.length > 1 ? `${product.name} — swipe or use arrow keys to view colourways` : undefined}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.img
            key={activeImage}
            src={activeImage}
            alt={`${product.name}${current.name ? ` in ${current.name}` : ""}`}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src && !e.currentTarget.src.endsWith(product.swatch)) {
                e.currentTarget.src = product.swatch;
              }
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, x: dragX }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
              x: dragX === 0 ? { type: "spring", stiffness: 350, damping: 30 } : { duration: 0 },
            }}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </AnimatePresence>

        <Link
          to={`/collection/${product.id}`}
          data-testid={`collection-image-link-${product.id}`}
          aria-label={`View ${product.name} specification`}
          className="absolute inset-0 z-[5]"
        />

        <span className="absolute top-3 left-3 text-[#C9A876] text-[9px] tracking-[0.3em] uppercase px-2 py-1 bg-[#121315]/60 backdrop-blur border border-[#8C7355]/30 z-10">
          {product.discipline}
        </span>

        {colours.length > 1 && (
          <>
            <button
              data-testid={`colour-prev-${product.id}`}
              onClick={() => goto(idx - 1)}
              aria-label={`Previous colourway for ${product.name}`}
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-[#121315]/55 backdrop-blur border border-[#8C7355]/40 text-[#F5F4F0] opacity-0 group-hover:opacity-100 hover:bg-[#8C7355]/60 focus:opacity-100 z-10"
              style={{ transition: "opacity 0.4s ease, background-color 0.3s ease" }}
            >
              <ChevronLeft size={18} strokeWidth={1.6} />
            </button>
            <button
              data-testid={`colour-next-${product.id}`}
              onClick={() => goto(idx + 1)}
              aria-label={`Next colourway for ${product.name}`}
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-[#121315]/55 backdrop-blur border border-[#8C7355]/40 text-[#F5F4F0] opacity-0 group-hover:opacity-100 hover:bg-[#8C7355]/60 focus:opacity-100 z-10"
              style={{ transition: "opacity 0.4s ease, background-color 0.3s ease" }}
            >
              <ChevronRight size={18} strokeWidth={1.6} />
            </button>
            <span className="absolute bottom-3 right-3 text-[#F5F4F0] text-[9px] tracking-[0.28em] uppercase px-2 py-1 bg-[#121315]/55 backdrop-blur border border-[#8C7355]/30 z-10">
              {idx + 1} / {colours.length}
            </span>
          </>
        )}
      </div>

      <div className="p-5">
        <Link
          to={`/collection/${product.id}`}
          data-testid={`collection-card-link-${product.id}`}
          className="block group/link"
        >
          <p className="text-[#D6D1CA]/50 text-[10px] tracking-[0.3em] uppercase mb-2">{product.tag}</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-xl font-light tracking-tight group-hover/link:text-[#C9A876] transition-colors">
            {product.name}
          </h3>
          <p className="text-[#D6D1CA]/60 text-xs mt-1.5 font-body">{product.finish}</p>
        </Link>

        {colours.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#8C7355]/12">
            <div
              className="flex items-center gap-1.5 h-4"
              data-testid={`collection-colours-${product.id}`}
            >
              {colours.map((c, ci) => {
                const on = ci === idx;
                return (
                  <button
                    key={c.name}
                    aria-label={c.name}
                    aria-pressed={on}
                    data-testid={`colour-${product.id}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => goto(ci)}
                    className="w-6 h-2.5 rounded-sm border focus:outline-none"
                    style={{
                      backgroundColor: c.hex,
                      borderColor: on ? "#C9A876" : "rgba(245,244,240,0.15)",
                      transform: on ? "scaleY(1.5)" : "scaleY(1)",
                      transition: "border-color 0.3s ease, transform 0.3s ease",
                    }}
                  />
                );
              })}
            </div>
            <p className="text-[#C9A876]/85 text-[9px] tracking-[0.28em] uppercase mt-2 h-3">
              {current.name || `${colours.length} colourways`}
            </p>
          </div>
        )}

        <Link
          to={`/collection/${product.id}`}
          data-testid={`collection-view-specs-${product.id}`}
          className="inline-block mt-4 text-[#C9A876] text-[9px] tracking-[0.28em] uppercase link-underline hover:text-[#dcb884] transition-colors"
        >
          View specification →
        </Link>
      </div>
    </motion.article>
  );
}
